// Local DTOs that mirror the backend until @medone/medonehp-api-client republishes
// with the new QrdaImportClient. Once that lands, replace these with re-exports.

export enum QrdaImportRunStatuses {
    NotStarted = 1,
    Running = 2,
    Completed = 3,
    Failed = 4,
}

export enum QrdaImportPatientStatuses {
    Pending = 1,
    Parsing = 2,
    Validating = 3,
    Computing = 4,
    Completed = 5,
    Error = 6,
    Quarantined = 7,
    Skipped = 8,
}

export enum QrdaImportFileStatuses {
    Pending = 1,
    ParseError = 2,
    Parsed = 3,
}

export enum QrdaImportUploadKinds {
    SingleFile = 1,
    Zip = 2,
}

export enum CqmResultCalcSources {
    NoteSave = 1,
    Import = 2,
    ManualRecompute = 3,
}

export interface QrdaValidationErrorDto {
    code: string;
    severity: string;
    xPath?: string | null;
    line?: number | null;
    column?: number | null;
    message: string;
}

export interface QrdaImportRunFileDto {
    id: number;
    qrdaImportRunId: number;
    qrdaImportRunPatientId?: number | null;
    entryName: string;
    status: QrdaImportFileStatuses;
    errorsCount: number;
    warningsCount: number;
    bytesProcessed: number;
    createdAt: string;
    errors: QrdaValidationErrorDto[];
}

export interface QrdaImportRunPatientMeasureResultDto {
    measureCmsId: string;
    cqmMeasureResultVersionId: number;
    version: number;
    ipp: boolean;
    denom: boolean;
    denex: boolean;
    denexcep: boolean;
    num: boolean;
    numex: boolean;
    /**
     * Per-population cardinality: 0/1 for patient-based measures, the per-episode count for
     * encounter-basis measures (CMS22v14/CMS68v15). Null on rows serialized before counts
     * existed — render the booleans in that case.
     */
    ippCount?: number | null;
    denomCount?: number | null;
    denexCount?: number | null;
    denexcepCount?: number | null;
    numCount?: number | null;
    numexCount?: number | null;
}

export interface QrdaImportRunPatientDto {
    id: number;
    qrdaImportRunId: number;
    /** Opaque 16-char SHA-256 prefix. NEVER contains name/DOB/MRN. */
    opaquePatientKey: string;
    matchedPatientId?: number | null;
    status: QrdaImportPatientStatuses;
    errorMessage?: string | null;
    isQuarantined: boolean;
    createdAt: string;
    measureResults: QrdaImportRunPatientMeasureResultDto[];
}

export interface QrdaImportRunDto {
    id: number;
    name: string;
    createdById: string;
    createdByName?: string | null;
    createdAt: string;
    status: QrdaImportRunStatuses;
    uploadKind: QrdaImportUploadKinds;
    originalFileName: string;
    dateStarted?: string | null;
    dateCompleted?: string | null;
    fileCount: number;
    patientCount: number;
    successCount: number;
    errorCount: number;
    failureReason?: string | null;
    files: QrdaImportRunFileDto[];
    patients: QrdaImportRunPatientDto[];
}

export interface QrdaImportRunsSearchDto {
    current?: number;
    pageSize?: number;
    status?: QrdaImportRunStatuses | null;
    createdByMe?: boolean | null;
}

export interface VsacBundleVersionDto {
    oid: string;
    version: string;
}

export interface CqmMeasureResultVersionDto {
    id: number;
    // Null when calcSource is Import (S13 Model 1 — Cypress decks have no native Patient row).
    patientId: number | null;
    cqmMeasureId: number;
    measureCmsId: string;
    year: number;
    version: number;
    isLatest: boolean;
    ipp: boolean;
    denom: boolean;
    denex: boolean;
    denexcep: boolean;
    num: boolean;
    numex: boolean;
    measureVersion: string;
    measureHqmfUuid: string;
    vsacBundleVersions: VsacBundleVersionDto[];
    provenanceJson: string;
    calcSource: CqmResultCalcSources;
    importRunId?: number | null;
    importRunPatientId?: number | null;
    capturedSummary?: string | null;
    sourceSection?: string | null;
    gapHintsJson?: string | null;
    calcTimestamp: string;
}

// Shape of the deserialized `provenanceJson` string on CqmMeasureResultVersionDto. Built by the
// backend QrdaImportProvenanceBuilder. PascalCase keys mirror the C# serializer (no camelCase
// policy). All fields optional — older rows carry only a subset until re-imported.

/** A code + system + sanitized terminology display label. */
export interface ProvenanceCodedRef {
    Code?: string | null;
    System?: string | null;
    Display?: string | null;
}

/** One clinical fact surfaced in the provenance, enriched beyond the bare code. */
export interface ProvenanceCodedFact {
    Code?: string | null;
    System?: string | null;
    Display?: string | null;
    /** Sub-bucket discriminator, e.g. 'diagnosis' | 'administered' | 'diagnosticStudy'. */
    Category?: string | null;
    Start?: string | null;
    End?: string | null;
    /** Present (true) only when the fact was negated ("not done"); omitted otherwise. */
    Negated?: boolean | null;
    NegationReason?: ProvenanceCodedRef | null;
    Rank?: number | null;
    Result?: ProvenanceCodedRef | null;
    Severity?: ProvenanceCodedRef | null;
    Route?: ProvenanceCodedRef | null;
}

/**
 * One engine-attributed contributing fact (SCRUM-700 item 3). Members live once in the shared
 * ContributingFacts pool; PopulationContributions maps short population keys to indices into it.
 * PHI-safe by construction — code/OID/label/date/value-set only, never subject, DOB, provider,
 * or free text (mirrors the CqmProvenanceBuilder CodedFact posture plus the driving ViaDefine).
 */
export interface AttributedFact {
    Code: string;
    /** Terminology system as an OID (URL→OID normalized by the engine). */
    System: string;
    Display: string;
    /** Lowercased FHIR resource type: 'encounter' | 'condition' | 'observation' | 'procedure' | 'servicerequest' | 'medicationrequest' | 'immunization'. */
    Category: string;
    Start?: string;
    End?: string;
    /** Present (true) only when the fact was negated ("not done"); omitted otherwise. */
    Negated?: true;
    /** CQL define whose closure retrieved this fact. */
    ViaDefine: string;
}

/** PHI-safe summary of one CQL define's outcome — the "why" behind a population. */
export interface ProvenanceDefineResult {
    Name: string;
    /** 'boolean' | 'list' | 'scalar' | 'null'. */
    Kind: string;
    Value?: boolean | null;
    Count?: number | null;
    Truthy: boolean;
}

export interface ProvenanceSnapshot {
    MeasureVersion?: string;
    MeasureHqmfUuid?: string;
    CalcTimestamp?: string;
    /** eCQM engine build + lowercase-hex SHA-256 of the calc result. Non-PHI; absent on rows
     *  written before SCRUM-700 added them to the snapshot. */
    EngineVersion?: string;
    ResultHash?: string;
    SourceImportRunPatientId?: number | null;
    ReportingPeriodStart?: string | null;
    ReportingPeriodEnd?: string | null;
    /** Short population keys (ipp/denom/denex/denexcep/num/numex) -> boolean. */
    Populations?: Record<string, boolean>;
    /**
     * Short population keys -> cardinality (0/1 patient-based, per-episode count for
     * encounter-basis measures). Absent on boolean-era snapshots — fall back to Populations.
     */
    PopulationCounts?: Record<string, number>;
    /** The measure's population basis ("boolean" / "Encounter") as evaluated. */
    PopulationBasis?: string | null;
    /** FHIR population code (initial-population, etc.) -> driving CQL define name. */
    PopulationDefines?: Record<string, string>;
    /** What initiated the calc (e.g. 'Import', 'NoteSave', 'Sweep'). Written by the backend from SCRUM-700 on. */
    Trigger?: string;
    /** Sweep event log row that produced this calc, when Trigger is a sweep. */
    SweepEventLogId?: number;
    Defines?: ProvenanceDefineResult[];
    /**
     * Engine attribution (SCRUM-700 item 3), additive and omitted when empty — absent on
     * legacy/thin rows and rows predating item 3. ContributingFacts is the shared fact pool;
     * PopulationContributions maps each short population key to indices into it (many-to-many).
     * Rendered per-population when present; otherwise the shared contributing-data list is the
     * fallback. Raw passthrough — the client does not compute these, only reads them.
     */
    ContributingFacts?: AttributedFact[];
    PopulationContributions?: Record<string, number[]>;
    Encounters?: ProvenanceCodedFact[];
    Conditions?: ProvenanceCodedFact[];
    Medications?: ProvenanceCodedFact[];
    Procedures?: ProvenanceCodedFact[];
    Immunizations?: ProvenanceCodedFact[];
    Assessments?: ProvenanceCodedFact[];
}

export interface QualityReportingImportsState {
    loading: boolean;
    processing: boolean;
    errorMessage: string | null;
    runs: QrdaImportRunDto[];
    runsTotal: number;
    currentRun: QrdaImportRunDto | null;
}

export const initialQualityReportingImportsState: QualityReportingImportsState = {
    loading: false,
    processing: false,
    errorMessage: null,
    runs: [],
    runsTotal: 0,
    currentRun: null,
};
